import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Button, Stack } from '@mui/material'
import { useDeleteIdsHealthcareservicesHcsIdRequestersId } from '@toktokdoctech/tokapi-client'
import { UserRpps } from '@saas/interfaces/core'
import { Banner } from '@workspace/frontend-kit'
import {
    RequesterData,
    RequesterFormDisclaimerContent,
    RequesterFormDisclaimerProps,
    RequesterFormProps,
} from './_props'
import FormContainer from '../Common/form/formContainer/FormContainer'
import {
    computeSavableRequesterData,
    DEFAULT_ERROR_MESSAGE,
    defaultRppsData,
    deletionBanner,
    GLOBAL_ERROR_MESSAGE,
    MISSING_FIELDS_ERROR_MESSAGE,
    REQUESTER_FORM_TITLE,
    SCROLL_OFFSET,
    SCROLL_TIMEOUT,
    SEARCH_RPPS_TITLE,
} from './utils/_utils'
import SearchRpps from '../Common/searchRpps/SearchRpps'
import FormSection from '../Common/form/formSection/FormSection'
import LastName from './fields/LastName'
import FirstName from './fields/FirstName'
import Profession from './fields/Profession'
import Speciality from './fields/Speciality'
import Rpps from './fields/Rpps'
import Idnps from './fields/Idnps'
import CodePractice from './fields/CodePractice'
import FinessNum from './fields/FinessNum'
import AmNum from './fields/AmNum'
import CommonLoaderBackdrop from '../Common/CommonLoaderBackdrop'
import SaveComponent from '../Common/SaveComponent/SaveComponent'
import { useRequesterFormContext } from './utils/requesterFormContext'
import useFormUtils from '../Common/form/_utils/useFormUtils'
import { useAppDispatch } from '../../store'
import { useRequesterRpps } from '../../hooks/useRequesterRpps'
import useUserFormGlobalError from '../User/utils/useUserFormGlobalError'
import sxs from './_sxs'
import { useRequesterFormValidation } from '../../hooks/useRequesterFormValidation'
import { displaySnackbar } from '../../actions/main'
import useCurrentHcs from '../../hooks/useCurrentHcs'
import useUserPermissions from '../../hooks/useUserPermissions'
import DeletionBanner from '../Common/DeletionBanner/DeletionBanner'
import { useRequesterMutation } from '../../hooks/useRequesterMutation'
import { handleExternal, RouterRouteId } from '../../routers/_props'
import SecuredEmail from './fields/SecuredEmail'
import RequesterFormDisclaimer from './RequesterFormDisclaimer'

const RequesterForm = ({ mode, data }: RequesterFormProps) => {
    const { hasUserPermission } = useUserPermissions()
    const { update, state } = useRequesterFormContext()
    const editMode = state.isEditionMode
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const currentHcs = useCurrentHcs()
    const params = useParams<RouterRouteId>()
    const requesterId = params.id ?? ''

    const { setGlobaleErrorMessage, clearGlobaleErrorMessage, globalErrorMessage } = useUserFormGlobalError()
    const [userRpps, setUserRpps] = useState<Partial<RequesterData> | undefined>(undefined)
    const [searchRppsHasError, setSearchRppsHasError] = useState(false)
    const [formLevelError, setFormLevelError] = useState<string | undefined>()
    const [highLightedSectionId, setHighLightedSectionId] = useState<string | undefined>(undefined)
    const [highLightedSectionTimeoutId, setHighLightedSectionTimeoutId] = useState<
        ReturnType<typeof setTimeout> | undefined
    >(undefined)

    const form = useForm<RequesterData>({
        mode: 'onChange',
        defaultValues: data,
    })
    const { resetForm } = useFormUtils<RequesterData>(form)
    const { loadRpps } = useRequesterRpps({ resetForm, update })
    const { triggerForm } = useRequesterFormValidation({
        form,
        setGlobal: setGlobaleErrorMessage,
        clearGlobal: clearGlobaleErrorMessage,
    })

    const {
        formState: { errors },
        handleSubmit,
        reset,
        watch,
        getValues,
    } = form

    const watched = {
        codePractice: watch('codePractice'),
        rppsNum: watch('rppsNum'),
    }

    const formValues = getValues()
    const hasSecuredEmailWarning = currentHcs?.isRequesterValidationEnabled && !formValues.securedEmail

    const { mutate } = useRequesterMutation(editMode, state.isExternal)

    const { mutate: deleteRequester, isPending: isDeletingRequester } = useDeleteIdsHealthcareservicesHcsIdRequestersId(
        {
            mutation: {
                onMutate: () => {
                    update({ isLoading: true, loaderMessage: 'Suppression en cours' })
                },
                onSuccess: () => {
                    dispatch(displaySnackbar('success', 'Le requérant a été supprimé avec succès'))
                    navigate('/requesters')
                },
                onError: (err: unknown) => {
                    const message = err instanceof Error ? err.message : GLOBAL_ERROR_MESSAGE
                    dispatch(displaySnackbar('error', message))
                    setGlobaleErrorMessage(message)
                },
                onSettled: () => {
                    update({ isLoading: false, loaderMessage: undefined })
                },
            },
        },
    )

    const hasFormError = errors !== undefined && Object.keys(errors).length > 0
    const isLoading = state.isSaving || state.isLoading
    const bannerMessage =
        globalErrorMessage || formLevelError || (hasFormError ? MISSING_FIELDS_ERROR_MESSAGE : undefined)

    const handleRppsChange = async (value: string | UserRpps | null) => {
        if (!value || typeof value === 'string') {
            resetForm(defaultRppsData)
            update({ isLoading: false })
            return undefined
        }

        setFormLevelError(undefined)
        const rppsData = await loadRpps(value)
        setUserRpps(rppsData)
    }

    useEffect(() => {
        if (editMode && data) {
            reset(data)
        }
    }, [editMode, data, reset])

    useEffect(() => {
        if (!state.isEditionMode) {
            update({
                isEditionMode: mode === 'EDITION',
            })
        }
    }, [mode])

    const handleSaveTrigger = async () => {
        const ok = await triggerForm()

        if (!ok || (!editMode && !userRpps)) {
            setSearchRppsHasError(true)
            setFormLevelError(MISSING_FIELDS_ERROR_MESSAGE)

            return
        }

        handleSubmit((savables) => {
            const hcsId = currentHcs?.id || ''
            const computedData = computeSavableRequesterData(savables, hcsId)
            mutate({ id: requesterId, hcsId, data: computedData })
        })()
    }

    const handleCancel = () => {
        update({
            shouldBlockNavigation: false,
        })
        if (state.isExternal) {
            handleExternal('cancel')
        } else if (!state.isExternal) navigate('/requesters')
    }

    const handleDelete = () => {
        const hcsId = currentHcs?.id || ''

        deleteRequester({
            hcsId,
            id: requesterId,
        })
    }

    const handleHighlight = (targetID: string) => {
        if (highLightedSectionTimeoutId) clearTimeout(highLightedSectionTimeoutId)
        setHighLightedSectionId(targetID)

        const target = document.getElementById(targetID)

        if (target) {
            window.scrollTo({
                top: target.offsetTop + SCROLL_OFFSET,
                behavior: 'smooth',
            })
        }

        const timeoutId = setTimeout(() => {
            setHighLightedSectionId(undefined)
            clearTimeout(timeoutId)
        }, SCROLL_TIMEOUT)

        setHighLightedSectionTimeoutId(timeoutId)
    }

    const getDisclaimerProps = (): RequesterFormDisclaimerProps => {
        const contents: RequesterFormDisclaimerContent[] = []

        if (hasSecuredEmailWarning) {
            const content: RequesterFormDisclaimerContent = {
                id: 'securedEmailWarning',
                text: (
                    <>
                        Renseignez{' '}
                        <Button
                            title="&#x2693; Ancre vers la section: email sécurisé"
                            color="inherit"
                            onClick={() => handleHighlight('securedEmailAnchor')}
                            sx={sxs.requesterFormDisclaimerButton}
                        >
                            ci-dessous
                        </Button>{' '}
                        les informations manquantes.
                    </>
                ),
            }
            contents.push(content)
        }
        return {
            displayed: editMode && contents.length > 0,
            contents: contents,
        }
    }

    return (
        <>
            <FormProvider {...form}>
                <Stack sx={{ gap: state.isExternal ? '2.125rem' : 0 }}>
                    {!editMode && (
                        <FormContainer sx={{ ...sxs.requesterForm, ...(!state.isExternal && { marginY: '2.125rem' }) }}>
                            <SearchRpps
                                title={SEARCH_RPPS_TITLE}
                                displayTitle
                                onRppsUserChange={handleRppsChange}
                                selectedValue={undefined}
                                codeprofession={undefined}
                                required
                                error={searchRppsHasError && !userRpps}
                                errorMessage={DEFAULT_ERROR_MESSAGE}
                            />
                        </FormContainer>
                    )}
                    <Box sx={sxs.requesterFormContainer}>
                        <FormContainer sx={sxs.requesterForm}>
                            {editMode && <RequesterFormDisclaimer {...getDisclaimerProps()} />}
                            <FormSection label={REQUESTER_FORM_TITLE}>
                                <LastName />
                                <FirstName />
                                <Profession />
                                <Speciality />
                                <Rpps />
                                <Idnps />
                                {currentHcs?.isRequesterValidationEnabled && <SecuredEmail />}
                                <CodePractice />
                                {watched.codePractice === 'Employee' && <FinessNum />}
                                {watched.codePractice === 'Liberal' && <AmNum />}
                            </FormSection>
                            <Stack alignItems="center" gap="1.5rem" sx={{ width: '100%' }}>
                                {bannerMessage && (
                                    <Box
                                        sx={{
                                            minWidth: {
                                                xs: 'fit-content',
                                                lg: '34rem',
                                            },
                                            width: '75%',
                                            marginTop: '3.5rem',
                                            whiteSpace: 'pre-line',
                                        }}
                                    >
                                        <Banner
                                            severity="ERROR"
                                            title={bannerMessage}
                                            slotOverrides={{
                                                container: {
                                                    border: 'none',
                                                },
                                            }}
                                        />
                                    </Box>
                                )}
                                <SaveComponent
                                    withMargin={false}
                                    saveButton={{
                                        action: () => {
                                            handleSaveTrigger()
                                        },
                                    }}
                                    cancelButton={{ action: handleCancel }}
                                    disableAll={isLoading}
                                    shouldBlockNavigation={state.shouldBlockNavigation}
                                />
                            </Stack>
                        </FormContainer>
                        {editMode && hasUserPermission('ADMIN') && (
                            <DeletionBanner
                                bannerTitle={deletionBanner.title}
                                bannerLabel={deletionBanner.label}
                                bannerSubLabel={deletionBanner.subLabel}
                                onConfirm={handleDelete}
                                isPending={isDeletingRequester}
                            />
                        )}
                    </Box>
                </Stack>
            </FormProvider>
            {isLoading && <CommonLoaderBackdrop open label={state.loaderMessage} />}
        </>
    )
}

export default RequesterForm
